// Helpers for API client (OAuth 2.0 client credentials) scopes, IP ranges and
// secret previews. Config/constants live in `constants/apiClient`.
import { API_SCOPE_ACTIONS, API_SCOPE_ENTITIES, ApiScopeAction } from 'constants/apiClient'

export const buildScope = (entity: string, action: ApiScopeAction): string => `${entity}:${action}`

export const parseScope = (scope: string): { entity: string; action: string } => {
  const [entity, action] = scope.split(':')
  return { entity, action }
}

const entityLabel = (key: string): string => API_SCOPE_ENTITIES.find((e) => e.key === key)?.label || key

// Groups a flat scope list into one entry per entity with its actions ordered
// like `API_SCOPE_ACTIONS`. Used to render a single chip per entity.
export const groupScopesByEntity = (scopes: string[] = []): { entity: string; label: string; actions: string[] }[] => {
  const map = new Map<string, string[]>()

  scopes.forEach((scope) => {
    const { entity, action } = parseScope(scope)

    if (!entity || !action) {
      return
    }

    map.set(entity, [...(map.get(entity) || []), action])
  })

  return [...map.entries()].map(([entity, actions]) => ({
    entity,
    label: entityLabel(entity),
    actions: [...actions].sort(
      (a, b) => API_SCOPE_ACTIONS.indexOf(a as ApiScopeAction) - API_SCOPE_ACTIONS.indexOf(b as ApiScopeAction)
    )
  }))
}

// Every non-read action selected on an entity requires that entity's `read`
// scope to be selected too. Returns an error string (or undefined when valid).
export const validateScopes = (scopes: string[] = []): string | undefined => {
  if (!scopes.length) {
    return 'Select at least one scope'
  }

  const byEntity = scopes.reduce<Record<string, Set<string>>>((acc, scope) => {
    const { entity, action } = parseScope(scope)

    acc[entity] = acc[entity] || new Set()
    acc[entity].add(action)

    return acc
  }, {})

  const missingRead = Object.entries(byEntity)
    .filter(([, actions]) => !actions.has('read') && [...actions].some((action) => action !== 'read'))
    .map(([entity]) => entity)

  if (missingRead.length) {
    const labels = missingRead.map(entityLabel)

    return `${labels.join(', ')}: "read" is required alongside the other selected actions`
  }
}

const IP_OR_CIDR = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(\/(\d|[12]\d|3[0-2]))?$/

// Single-value validator for one IP / CIDR entry (used per-field in the FieldArray).
export const validateIp = (value?: string): string | undefined => {
  if (!value) {
    return
  }

  const match = value.trim().match(IP_OR_CIDR)

  if (!match || [match[1], match[2], match[3], match[4]].some((octet) => Number(octet) > 255)) {
    return 'Invalid IP / CIDR'
  }
}

// `abc123...` -> `abc1**********`
export const maskSecretPreview = (preview?: string | null): string => {
  if (!preview) {
    return '—'
  }

  return `${preview.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4)}${'*'.repeat(10)}`
}
